import * as THREE from 'three'
import { ROCK_COLOR_URL, ROCK_NORMAL_URL } from './rocktex'

/**
 * The boulder surface: a photographed rock (ambientCG Rock031, CC0), projected
 * triplanar from world space.
 *
 * Boulders were the worst-looking surface in the game — `IcosahedronGeometry`
 * with a flat vertex colour, which at any distance read as a grey golf ball
 * sitting on the snow. They are also the cheapest thing to fix properly: a
 * couple of hundred instances, a metre or two across, rarely more than a few
 * percent of the frame.
 *
 * **Triplanar rather than UVs.** A jittered icosahedron's UVs have a seam and
 * heavy distortion at the poles, which on rock reads as a smear. Projecting
 * from world position costs three samples per map instead of one and has no
 * seam anywhere.
 *
 * **World space rather than object space**, which matters more than it looks:
 * every boulder shares one geometry and one draw call, so an object-space
 * projection would paint all of them identically and a hillside would read as
 * the same rock stamped two hundred times. Anchoring to world position means
 * each one samples a different part of the texture for free.
 *
 * The snow cap stays untouched. The geometry already bakes snow into its
 * vertex colours by face angle, so the texture is blended in by how *dark* the
 * vertex colour is — rock gets the photograph, the snow on top stays snow.
 */

const TRIPLANAR_SCALE = 0.55 // texture repeats per metre
/** Mean luminance of the baked albedo, reported by tools/make-rocktex.mjs. */
const ROCK_MEAN_LUMINANCE = 0.176
/** Scales the photograph to mean 1.0 so it modulates rather than darkens. */
const ROCK_GAIN = 1 / ROCK_MEAN_LUMINANCE

function loadTexture(url: string, srgb: boolean): THREE.Texture {
  const tex = new THREE.TextureLoader().load(url)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  // Albedo is sRGB-encoded; a normal map is directions and must stay linear.
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace
  tex.anisotropy = 4
  return tex
}

// Decoded once for the life of the page. A slope switch rebuilds every mesh,
// and creating these per build uploaded a fresh copy of both maps to the GPU
// each time while the old pair stayed resident — the material is disposed on
// teardown, its textures were not.
let sharedColor: THREE.Texture | null = null
let sharedNormal: THREE.Texture | null = null

export function createRockMaterial(): THREE.MeshStandardMaterial {
  sharedColor ??= loadTexture(ROCK_COLOR_URL, true)
  sharedNormal ??= loadTexture(ROCK_NORMAL_URL, false)
  const map = sharedColor
  const normalMap = sharedNormal

  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true, roughness: 0.92, metalness: 0,
  })

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uRockColor = { value: map }
    shader.uniforms.uRockNormal = { value: normalMap }

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
         varying vec3 vRockWorld;
         varying vec3 vRockNormal;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
         {
           mat4 inst = mat4(1.0);
           #ifdef USE_INSTANCING
             inst = instanceMatrix;
           #endif
           mat4 rockModel = modelMatrix * inst;
           vRockWorld = (rockModel * vec4(position, 1.0)).xyz;
           // mat3(mat4) is GLSL ES 3.00 only, and three compiles these shaders
           // as ES 1.00 even on a WebGL2 context. Take the basis columns by
           // hand instead.
           vRockNormal = normalize(
             mat3(rockModel[0].xyz, rockModel[1].xyz, rockModel[2].xyz) * normal);
         }`,
      )

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
         uniform sampler2D uRockColor;
         uniform sampler2D uRockNormal;
         varying vec3 vRockWorld;
         varying vec3 vRockNormal;

         // Blend weights, sharpened so the three projections do not smear
         // into each other across a whole face.
         vec3 rockWeights(vec3 n) {
           vec3 w = pow(abs(n), vec3(4.0));
           return w / max(w.x + w.y + w.z, 1e-4);
         }`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
         vec3 rockW = rockWeights(vRockNormal);
         vec2 rockUvX = vRockWorld.zy * ${TRIPLANAR_SCALE.toFixed(3)};
         vec2 rockUvY = vRockWorld.xz * ${TRIPLANAR_SCALE.toFixed(3)};
         vec2 rockUvZ = vRockWorld.xy * ${TRIPLANAR_SCALE.toFixed(3)};
         // How much of this vertex is rock rather than the baked snow cap.
         float rockness = 1.0 - smoothstep(0.50, 0.76,
           dot(vColor.rgb, vec3(0.299, 0.587, 0.114)));
         vec3 rockAlbedo =
             texture2D(uRockColor, rockUvX).rgb * rockW.x
           + texture2D(uRockColor, rockUvY).rgb * rockW.y
           + texture2D(uRockColor, rockUvZ).rgb * rockW.z;
         // Normalised to mean 1.0 and used as a *modulation*, not a
         // replacement. Dropped in raw, this photograph rendered the boulders
         // nearly black: it is physically based and measures 0.176 mean
         // luminance, against the 0.35 hand-picked grey the scene was lit and
         // exposed around. Dividing by its own mean keeps every bit of the
         // photographic variation while leaving the tonal placement — and the
         // cold vertex tint that suits the snow — exactly where it was.
         vec3 rockTex = min(rockAlbedo * ${ROCK_GAIN.toFixed(3)}, vec3(2.2));
         diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * rockTex, rockness);`,
      )
      .replace(
        '#include <normal_fragment_maps>',
        `#include <normal_fragment_maps>
         if (rockness > 0.01) {
           vec3 wn = vRockNormal;
           vec3 tX = texture2D(uRockNormal, rockUvX).xyz * 2.0 - 1.0;
           vec3 tY = texture2D(uRockNormal, rockUvY).xyz * 2.0 - 1.0;
           vec3 tZ = texture2D(uRockNormal, rockUvZ).xyz * 2.0 - 1.0;
           // UDN blend: cheap, stable, and does not need a tangent basis —
           // which this geometry does not have and should not have to grow.
           tX = vec3(tX.xy + wn.zy, wn.x);
           tY = vec3(tY.xy + wn.xz, wn.y);
           tZ = vec3(tZ.xy + wn.xy, wn.z);
           vec3 rockN = normalize(
             tX.zyx * rockW.x + tY.xzy * rockW.y + tZ.xyz * rockW.z);
           normal = normalize(mix(normal, mat3(viewMatrix) * rockN, rockness * 0.85));
         }`,
      )
  }
  return mat
}
